import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { projectsApi, itemsApi, listingsApi } from '../utils/api';
import { Spinner } from '../components/shared/Spinner';
import { ConfirmationModal } from '../components/shared/ConfirmationModal';
import { ItemDetailModal } from '../components/shared/ItemDetailModal';
import { format } from 'date-fns';

interface Project {
  _id: string;
  title: string;
  description?: string;
  address?: string;
  status: 'draft' | 'listed' | 'completed';
  items: any[];
  tags: string[];
  itemCount?: number;
  createdAt: string;
  updatedAt: string;
}


interface Item {
  _id: string;
  title: string;
  description?: string;
  condition: 'new' | 'used' | 'salvage';
  price: number;
  currency: string;
  materials: string[];
  images: string[];
  dimensions?: {
    width: number;
    height: number;
    depth: number;
    units: string;
  };
  aiAnalysis?: {
    estimatedValue: number;
    confidence: number;
    notes?: string;
  };
  createdAt: string;
  status: 'draft' | 'listed';
}

export const ProjectDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [project, setProject] = useState<Project | null>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [itemsLoading, setItemsLoading] = useState(true);
  const [bulkListingLoading, setBulkListingLoading] = useState(false);
  const [error, setError] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const [showItemModal, setShowItemModal] = useState(false);

  const loadProject = useCallback(async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      const response = await projectsApi.get(id);
      if (response.data.success && response.data.data) {
        setProject(response.data.data.project);
      } else {
        setError('Project not found');
      }
    } catch (error: any) {
      console.error('Failed to load project:', error);
      setError('Failed to load project');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadProjectItems = useCallback(async () => {
    if (!id) return;
    
    try {
      setItemsLoading(true);
      // Filter by both project and current user to ensure security
      const response = await itemsApi.list({ project: id, seller: user?.id });
      if (response.data.success) {
        setItems(response.data.data || []);
      }
    } catch (error: any) {
      console.error('Failed to load project items:', error);
    } finally {
      setItemsLoading(false);
    }
  }, [id, user?.id]);

  useEffect(() => {
    if (isAuthenticated && id && user) {
      loadProject();
      loadProjectItems();
    }
  }, [isAuthenticated, id, user, loadProject, loadProjectItems]);

  const handleItemSelect = (itemId: string) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const handleSelectAllItems = () => {
    if (selectedItems.size === items.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(items.map(item => item._id)));
    }
  };

  const handleBulkPostToListings = async () => {
    if (selectedItems.size === 0) return;

    setBulkListingLoading(true);
    try {
      const selectedItemsArray = Array.from(selectedItems);
      const promises = selectedItemsArray.map(async (itemId) => {
        const item = items.find(i => i._id === itemId);
        if (!item) return null;

        // Create listing from item
        const listingData = {
          title: item.title,
          description: item.description || 'No description available',
          price: item.price,
          condition: item.condition.charAt(0).toUpperCase() + item.condition.slice(1), // Capitalize first letter
          images: item.images,
          project: id,
          category: 'Other', // Default category - could be improved with AI categorization
          location: {
            address: '123 Main Street', // Default address - should be from user profile
            city: 'Auckland',
            region: 'Auckland',
            country: 'New Zealand'
          },
          quantity: 1,
          availableQuantity: 1,
          tags: item.materials || []
        };

        return listingsApi.create(listingData);
      });

      const results = await Promise.all(promises);
      const successCount = results.filter(r => r?.data.success).length;
      
      // Clear selection and reload items
      setSelectedItems(new Set());
      loadProjectItems();
      
    } catch (error: any) {
      console.error('Failed to post items to listings:', error);
      alert('Failed to post some items to listings. Please try again.');
    } finally {
      setBulkListingLoading(false);
    }
  };

  const handleBulkDeleteItems = () => {
    if (selectedItems.size === 0) return;
    setShowDeleteConfirm(true);
  };

  const confirmBulkDelete = async () => {
    setShowDeleteConfirm(false);
    setBulkListingLoading(true);
    
    try {
      const selectedItemsArray = Array.from(selectedItems);
      const promises = selectedItemsArray.map(async (itemId) => {
        return itemsApi.delete(itemId);
      });

      const results = await Promise.all(promises);
      const successCount = results.filter(r => r?.data.success).length;
      
      alert(`Successfully deleted ${successCount} item${successCount !== 1 ? 's' : ''}!`);
      
      // Clear selection and reload items
      setSelectedItems(new Set());
      loadProjectItems();
      
    } catch (error: any) {
      console.error('Failed to delete items:', error);
      alert('Failed to delete some items. Please try again.');
    } finally {
      setBulkListingLoading(false);
    }
  };

  const cancelBulkDelete = () => {
    setShowDeleteConfirm(false);
  };

  const handleItemClick = (item: Item) => {
    setSelectedItem(item);
    setShowItemModal(true);
  };

  const handleCloseItemModal = () => {
    setShowItemModal(false);
    setSelectedItem(null);
  };

  const handleItemUpdated = (updatedItem: Item) => {
    // Update the item in the items array
    setItems(prevItems => 
      prevItems.map(item => 
        item._id === updatedItem._id ? updatedItem : item
      )
    );
    // Update the selected item
    setSelectedItem(updatedItem);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300';
      case 'listed': return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300';
      case 'completed': return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300';
      default: return 'bg-slate-100 text-slate-800 dark:bg-slate-900/20 dark:text-slate-300';
    }
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'PPP p');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner />
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="p-6">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-4">
            {error || 'Project not found'}
          </h1>
          <Link to="/projects" className="btn-primary">
            Back to Projects
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/projects')}
              className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100">
              {project.title}
            </h1>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(project.status)}`}>
              {project.status}
            </span>
          </div>
        </div>

        {/* Project Info */}
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-3">Project Details</h2>
              {project.description && (
                <div className="mb-4">
                  <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Description</h3>
                  <p className="text-slate-600 dark:text-slate-400">{project.description}</p>
                </div>
              )}
              {project.address && (
                <div className="mb-4">
                  <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Address</h3>
                  <p className="text-slate-600 dark:text-slate-400">{project.address}</p>
                </div>
              )}
              <div className="mb-4">
                <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Created</h3>
                <p className="text-slate-600 dark:text-slate-400">{formatDate(project.createdAt)}</p>
              </div>
            </div>
            
            <div>
              <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-3">Statistics</h2>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-slate-600 dark:text-slate-400">Items</span>
                  <span className="font-medium text-slate-900 dark:text-slate-100">{items.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600 dark:text-slate-400">Status</span>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(project.status)}`}>
                    {project.status}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Tags */}
          {project.tags && project.tags.length > 0 && (
            <div className="mt-6 pt-6 border-t border-slate-200 dark:border-slate-700">
              <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Tags</h3>
              <div className="flex flex-wrap gap-2">
                {project.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-full text-sm"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Items Section */}
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
              Project Items ({items.length})
            </h2>
            {items.length > 0 && (
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleSelectAllItems}
                  className="btn-secondary text-sm"
                >
                  {selectedItems.size === items.length ? 'Deselect All' : 'Select All'}
                </button>
                 {selectedItems.size > 0 && (
                   <>
                     <button
                       onClick={handleBulkPostToListings}
                       disabled={bulkListingLoading}
                       className="btn-primary text-sm flex items-center"
                     >
                       {bulkListingLoading ? (
                         <>
                           <Spinner size="sm" className="mr-2" />
                           Posting...
                         </>
                       ) : (
                         <>
                           Post to Listings ({selectedItems.size})
                         </>
                       )}
                     </button>
                     <button
                       onClick={handleBulkDeleteItems}
                       disabled={bulkListingLoading}
                       className="btn-destructive text-sm flex items-center"
                     >
                       <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                       </svg>
                       Delete ({selectedItems.size})
                     </button>
                   </>
                 )}
              </div>
            )}
          </div>

          {itemsLoading ? (
            <div className="flex items-center justify-center h-32">
              <Spinner />
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-12">
              <svg className="w-16 h-16 text-slate-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
              <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">No items yet</h3>
              <p className="text-slate-600 dark:text-slate-400 mb-4">
                Create items for this project to see them here.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {items.map((item) => {
                const isSelected = selectedItems.has(item._id);
                const aiValue = item.aiAnalysis?.estimatedValue;
                
                return (
                   <div
                     key={item._id}
                     className={`border rounded-lg p-4 transition-all cursor-pointer ${
                       isSelected 
                         ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' 
                         : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                     }`}
                     onClick={(e) => {
                       // Check if click was on checkbox area
                       const target = e.target as HTMLElement;
                       const isCheckboxClick = target.closest('.checkbox-area');
                       
                       if (isCheckboxClick) {
                         handleItemSelect(item._id);
                       } else {
                         handleItemClick(item);
                       }
                     }}
                   >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-4">
                        {/* Selection Checkbox */}
                        <div className={`checkbox-area w-5 h-5 rounded border-2 flex items-center justify-center mt-1 ${
                          isSelected 
                            ? 'bg-primary-500 border-primary-500' 
                            : 'border-slate-300 dark:border-slate-600'
                        }`}>
                          {isSelected && (
                            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                        
                        {/* Item Image */}
                        <div className="w-16 h-16 bg-slate-200 dark:bg-slate-700 rounded-lg overflow-hidden flex-shrink-0 flex items-center justify-center">
                          {item.images && item.images.length > 0 ? (
                            <img
                              src={item.images[0]}
                              alt={item.title}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                          ) : null}
                          {/* Fallback icon - always present but only visible when image fails */}
                          <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                        
                        {/* Item Details */}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-slate-900 dark:text-slate-100 truncate">
                            {item.title}
                          </h3>
                          {item.description && (
                            <p className="text-sm text-slate-600 dark:text-slate-400 mt-1 line-clamp-2">
                              {item.description}
                            </p>
                          )}
                          <div className="flex items-center space-x-4 mt-2">
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              item.condition === 'new' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300' :
                              item.condition === 'used' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300' :
                              'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300'
                            }`}>
                              {item.condition}
                            </span>
                            {item.materials.length > 0 && (
                              <span className="text-xs text-slate-500 dark:text-slate-400">
                                {item.materials.slice(0, 2).join(', ')}
                                {item.materials.length > 2 && ` +${item.materials.length - 2} more`}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Price/Value */}
                      <div className="text-right flex-shrink-0 ml-4">
                        <div className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                          ${typeof aiValue === 'number' ? aiValue.toFixed(2) : item.price.toFixed(2)}
                        </div>
                        {typeof item.aiAnalysis?.confidence === 'number' && (
                          <div className="text-xs text-slate-500 dark:text-slate-400">
                            {Math.round(item.aiAnalysis.confidence * 100)}% confidence
                          </div>
                        )}
                        <div className={`text-xs px-2 py-1 rounded mt-1 ${
                          item.status === 'listed' 
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300'
                            : 'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-300'
                        }`}>
                          {item.status}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Delete Confirmation Modal */}
        <ConfirmationModal
          isOpen={showDeleteConfirm}
          title="Delete Items"
          message={`Are you sure you want to delete ${selectedItems.size} item${selectedItems.size !== 1 ? 's' : ''}? This action cannot be undone and will permanently remove the selected items from your project.`}
          confirmText={`Delete ${selectedItems.size} Item${selectedItems.size !== 1 ? 's' : ''}`}
          cancelText="Cancel"
          onConfirm={confirmBulkDelete}
          onCancel={cancelBulkDelete}
          isLoading={bulkListingLoading}
          variant="danger"
        />

        {/* Item Detail Modal */}
        <ItemDetailModal
          isOpen={showItemModal}
          onClose={handleCloseItemModal}
          item={selectedItem}
          onItemUpdated={handleItemUpdated}
        />

      </div>
    </div>
  );
};
